import React, { useMemo } from 'react';
import { message } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import ProjectSubmissionModal from '@/components/module/projects/ProjectSubmissionModal';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { PanelSectionLabel } from '@/pages/inside/tenant/style';
import { getTaskDisplayId, truncateFileName } from '@hooks/projects/projectHelpers';

/**
 * Queue tasks and notes before POST /projects/:projectId/qa/submit with submittedTo: pmo.
 */
export default function QaSubmitToPmoModal({
    open,
    onCancel,
    pmoSubmissionItems,
    pmoSubmissionDescriptionHtml,
    setPmoSubmissionDescriptionHtml,
    pmoSubmissionPendingFiles = [],
    pmoSubmissionPendingLinks = [],
    handlePmoFilesSelected,
    removePmoPendingFile,
    removePmoPendingLink,
    handlePmoLinkRequest,
    handlePmoEditorLinkRemoved,
    pmoLinkModalOpen,
    pmoLinkForm,
    closePmoLinkModal,
    handlePmoLinkModalSubmit,
    pmoSubmissionSession,
    removeFromPmoSubmission,
    submitPmoFromModal,
    submitToPMOPending,
    onOpenTaskDetail,
    isAttachment = true,
    isLink = true,
    isMention = true,
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
}) {
    const handleSubmit = () => {
        submitPmoFromModal({
            onSuccess: () => message.success('Submitted to PMO'),
            onError: (err) =>
                message.error(err?.response?.data?.message || err?.message || 'Could not submit to PMO'),
        });
    };

    const taskRows = useMemo(() => {
        const list = Array.isArray(pmoSubmissionItems) ? pmoSubmissionItems : [];
        return list.map((item) => ({
            key: String(item.id),
            taskIdLabel: getTaskDisplayId(item.task),
            title: item.task?.title ?? item.task?.name ?? '—',
            removeType: 'task',
            removeKey: item.id,
            payload: { sprint: item.sprint, task: item.task },
        }));
    }, [pmoSubmissionItems]);

    const handleTaskClick = (payload) => {
        if (!payload?.task || !onOpenTaskDetail) return;
        onOpenTaskDetail(payload.sprint, payload.task);
    };

    const sidLabel = `SID-${pmoSubmissionSession > 0 ? pmoSubmissionSession : 1}`;
    const submissionTitle = `Submission ${pmoSubmissionSession > 0 ? pmoSubmissionSession : 1}`;

    return (
        <>
            <MilestoneLinkModal
                open={pmoLinkModalOpen}
                onCancel={closePmoLinkModal}
                linkModalForm={pmoLinkForm}
                handleLinkModalSubmit={handlePmoLinkModalSubmit}
            />
            <ProjectSubmissionModal
                open={open}
                onCancel={onCancel}
                variant="pmo"
                modalSize="md"
                sidLabel={sidLabel}
                submissionTitle={submissionTitle}
                descriptionHtml={pmoSubmissionDescriptionHtml}
                onDescriptionChange={setPmoSubmissionDescriptionHtml}
                isAttachment={isAttachment}
                isLink={isLink}
                isMention={isMention}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                richTextEditorProps={{
                    showAttachButton: isAttachment,
                    onFilesSelected: isAttachment ? handlePmoFilesSelected : undefined,
                    onLinkRequest: isLink ? handlePmoLinkRequest : undefined,
                    onLinkRemoved:
                        isLink && typeof handlePmoEditorLinkRemoved === 'function' ? handlePmoEditorLinkRemoved : undefined,
                    onAttachmentRemoved:
                        isAttachment && typeof removePmoPendingFile === 'function'
                            ? (index) => {
                                  const p = pmoSubmissionPendingFiles[index];
                                  if (p) removePmoPendingFile(p.id);
                              }
                            : undefined,
                    attachmentIndexOffset: pmoSubmissionPendingFiles.length,
                }}
                afterDescription={
                    pmoSubmissionPendingFiles.length || pmoSubmissionPendingLinks.length ? (
                        <div
                            style={{
                                marginTop: 8,
                                marginBottom: 16,
                                padding: '8px 0',
                                borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0,
                            }}
                        >
                            {pmoSubmissionPendingFiles.length > 0 ? (
                                <>
                                    <PanelSectionLabel
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginBottom: 8,
                                            fontSize: 11,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            opacity: 0.75,
                                        }}
                                    >
                                        <FileOutlined />
                                        Attachments
                                    </PanelSectionLabel>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                            gap: 10,
                                        }}
                                    >
                                        {pmoSubmissionPendingFiles.map((p) => (
                                            <PendingAttachmentTile
                                                key={p.id}
                                                item={p}
                                                onRemove={() => removePmoPendingFile?.(p.id)}
                                                truncateFileName={truncateFileName}
                                                showBottomFileName={false}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : null}
                            {pmoSubmissionPendingLinks.length > 0 ? (
                                <div style={{ marginTop: pmoSubmissionPendingFiles.length > 0 ? 12 : 0 }}>
                                    <PendingLinkWrap links={pmoSubmissionPendingLinks} onRemoveLink={removePmoPendingLink} />
                                </div>
                            ) : null}
                        </div>
                    ) : null
                }
                taskRows={taskRows}
                onTaskRowClick={handleTaskClick}
                onRemoveTask={removeFromPmoSubmission}
                onSubmit={handleSubmit}
                submitPending={submitToPMOPending}
                emptyTasksMessage="Nothing selected yet."
            />
        </>
    );
}
